<template>
  <div>
    <accordion-panel
      class="chart-legends"
      header-class="chart-legends__header"
      :id="chartId"
      :collapsed.sync="collapsed"
    >
      <template #title>
        <strong> {{ $t('general.chart-legends') }} </strong>
      </template>
      <b-checkbox
        class="chart-legends__toggle-all"
        :checked="isAllLegendsSelected"
        @change="checkAllLegends"
      />
      <span>
        {{ $t('general.mark-chart-variables') }}
      </span>
      <ul class="chart-legends__list">
        <li
          v-for="(values, key) in orderedItems"
          :key="key"
          class="chart-legends__list-item"
        >
          <div :class="{ 'chart-legends__disabled': isLegendHidden(key) }">
            <b-checkbox
              class="chart-legends__checkbox"
              :checked="!isLegendHidden(values)"
              @change="newValue => toggleLegend(values, newValue)"
            />
            <span
              class="chart-legends__color"
              :style="getColorStyle(values[0].chartColor)"
            />
            <span class="chart-legends__checkbox-label">
              {{ key }}
            </span>
          </div>
        </li>
      </ul>
    </accordion-panel>

    <b-row
      v-if="enableManeuverListControls"
      class="chart-legends__search-row mx-0 mt-5"
    >
      <b-col cols="12" md="4" lg="3" class="px-0 ml-auto chart-legends__search-col">
        <s-search-input
          v-model="tempSearch"
          v-s-trigger="searchChange"
          :placeholder="$t('reports.oscillography.view-report.search')"
        />
      </b-col>
    </b-row>

    <accordion-panel
      v-if="!hideManeuversSection"
      class="chart-legends chart-legends__maneuvers-panel pa-0"
      header-class="chart-legends__header"
      :id="chartId"
      :withMarginHeader="false"
      :collapsed.sync="secondPanelCollapsed"
      :hasControlsSlot="true"
    >
      <template #controls>
        <b-dropdown-item @click="toogleAccordions()">
          {{ maneuverToggleAllLabel }}
        </b-dropdown-item>
      </template>
      <template #title>
        <b-row class="align-items-center py-4">
          <b-col cols="1">
            <b-checkbox
              class="chart-legends__toggle-all"
              :checked="isAllManeuversSelected"
              @change="checkAllManeuvers"
            />
          </b-col>
          <b-col v-if="oscillographyChartType === oscillographyChartTypes.OSCILLOGRAPHY" cols="2">
            <div
              class="preview justify-center align-center"
              :style="{background: background}"
            >
              <strong
                :style="{background: background}"
              >{{
                $t('reports.oscillography.view-report.graphic-line')
              }}</strong>
            </div>
          </b-col>
          <b-col
            :cols="maneuverNameColumnCols"
          >
            <button
              v-if="enableManeuverListControls"
              type="button"
              class="chart-legends__sort-button"
              :class="{
                'chart-legends__sort-button--active': sortBy.field === 'name',
              }"
              @click="sortByField('name')"
            >
              <strong>
                {{ $t('reports.oscillography.view-report.maneuver') }}
              </strong>
              <i :class="getSortIconClass('name')" />
            </button>
            <strong v-else>
              {{ $t('reports.oscillography.view-report.maneuver') }}
            </strong>
          </b-col>
          <b-col
            :cols="maneuverDateColumnCols"
          >
            <button
              v-if="enableManeuverListControls"
              type="button"
              class="chart-legends__sort-button"
              :class="{
                'chart-legends__sort-button--active':
                  sortBy.field === 'dateTimeOscillography',
              }"
              @click="sortByField('dateTimeOscillography')"
            >
              <strong>
                {{ $t('reports.oscillography.view-report.datetime') }}
              </strong>
              <i :class="getSortIconClass('dateTimeOscillography')" />
            </button>
            <strong v-else>
              {{ $t('reports.oscillography.view-report.datetime') }}
            </strong>
          </b-col>
          <b-col
            :cols="maneuverAssetColumnCols"
          >
            <button
              v-if="enableManeuverListControls"
              type="button"
              class="chart-legends__sort-button"
              :class="{
                'chart-legends__sort-button--active': sortBy.field === 'assetName',
              }"
              @click="sortByField('assetName')"
            >
              <strong>
                {{ $t('reports.oscillography.view-report.asset') }}
              </strong>
              <i :class="getSortIconClass('assetName')" />
            </button>
            <strong v-else>
              {{ $t('reports.oscillography.view-report.asset') }}
            </strong>
          </b-col>
          <b-col
            v-if="showInstallationAndCompanyColumns"
            cols="2"
          >
            <button
              v-if="enableManeuverListControls"
              type="button"
              class="chart-legends__sort-button"
              :class="{
                'chart-legends__sort-button--active': sortBy.field === 'installationName',
              }"
              @click="sortByField('installationName')"
            >
              <strong>
                {{ $t('reports.oscillography.view-report.installation') }}
              </strong>
              <i :class="getSortIconClass('installationName')" />
            </button>
            <strong v-else>
              {{ $t('reports.oscillography.view-report.installation') }}
            </strong>
          </b-col>
          <b-col
            v-if="showInstallationAndCompanyColumns"
            cols="1"
          >
            <button
              v-if="enableManeuverListControls"
              type="button"
              class="chart-legends__sort-button"
              :class="{
                'chart-legends__sort-button--active': sortBy.field === 'companyName',
              }"
              @click="sortByField('companyName')"
            >
              <strong>
                {{ $t('reports.oscillography.view-report.company') }}
              </strong>
              <i :class="getSortIconClass('companyName')" />
            </button>
            <strong v-else>
              {{ $t('reports.oscillography.view-report.company') }}
            </strong>
          </b-col>
          <b-col
            v-if="oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS"
            :cols="maneuverModuleColumnCols"
          >
            <button
              v-if="enableManeuverListControls"
              type="button"
              class="chart-legends__sort-button"
              :class="{
                'chart-legends__sort-button--active': sortBy.field === 'moduleName',
              }"
              @click="sortByField('moduleName')"
            >
              <strong>
                {{ $t('reports.oscillography.view-report.module') }}
              </strong>
              <i :class="getSortIconClass('moduleName')" />
            </button>
            <strong v-else>
              {{ $t('reports.oscillography.view-report.module') }}
            </strong>
          </b-col>
        </b-row>
      </template>

      <ul class="px-0">
        <li
          v-for="(item, index) in paginatedManeuvers"
          class="chart-legends__list-item px-0"
          :key="getRenderedManeuverKey(item, index)"
        >
          <accordion-panel
            class="pa-0"
            header-class="pa-0"
            :id="`chart-item-${getRenderedManeuverKey(item, index)}`"
            :collapsed="collapsedItems[getRenderedManeuverKey(item, index)]"
            @update:collapsed="
              toggleCollapse(getRenderedManeuverKey(item, index))
            "
            @update:collapsedprint="
              toggleCollapsePrint(getRenderedManeuverKey(item, index))
            "
          >
            <template #title>
              <b-row class="align-items-center">
                <b-col cols="1">
                  <b-checkbox
                    class="chart-legends__toggle-all"
                    :key="getManeuverCheckboxKey(item, index)"
                    :checked="isManeuverHidden(getManeuverKey(item))"
                    @change="
                      newValue => toggleManeuvers(getManeuverKey(item), newValue)
                    "
                  />
                </b-col>
                <b-col
                  v-if="oscillographyChartType === oscillographyChartTypes.OSCILLOGRAPHY"
                  cols="2"
                >
                  <div
                    v-if="bookmarks"
                    class="preview"
                    v-html="previewContent(
                      item.preview.lineTypesBookmark,
                      item.preview.lineWidth,
                      item.preview.lineSymbols,
                      item.preview.lineSymbolColor
                    )"
                  />
                  <div
                    v-else
                    class="preview"
                    v-html="previewContent(item.preview.lineTypes, 1, 'none', 'none')"
                  />
                </b-col>
                <b-col
                  :cols="maneuverNameColumnCols"
                >
                  <span> {{ item.name }} </span>
                </b-col>
                <b-col
                  :cols="maneuverDateColumnCols"
                >
                  <strong>
                    {{ formatDateTime(item.dateTimeOscillography) }}
                  </strong>
                </b-col>
                <b-col
                  :cols="maneuverAssetColumnCols"
                >
                  <strong> {{ item.assetName }} </strong>
                </b-col>
                <b-col
                  v-if="showInstallationAndCompanyColumns"
                  cols="2"
                >
                  <strong> {{ item.installationName }} </strong>
                </b-col>
                <b-col
                  v-if="showInstallationAndCompanyColumns"
                  cols="1"
                >
                  <strong> {{ item.companyName }} </strong>
                </b-col>
                <b-col
                  v-if="oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS"
                  :cols="maneuverModuleColumnCols"
                >
                  <strong> {{ item.moduleName }} </strong>
                </b-col>
              </b-row>
            </template>
            <div
              v-if="!collapsedItems[getRenderedManeuverKey(item, index)]"
              class="chart-legends__maneuver-details"
            >
              <template v-if="shouldShowDiagnosticsCards">
                <div
                  v-for="(field, fieldIndex) in getDiagnosticsFields(item)"
                  :key="`${field.name}-${fieldIndex}`"
                  class="mr-2 mb-3"
                >
                  <info-report-card
                    :value="getDiagnosticsFieldValue(field.value)"
                    :description="field.name"
                    :acceptable="isDiagnosticFieldAcceptable(field)"
                  />
                </div>
                <s-empty-list v-if="!getDiagnosticsFields(item).length" class="w-100" />
              </template>
              <template v-else>
                <b-badge
                  class="assetBadge"
                  pill
                  v-for="assetItem in filteredAssetInfo(
                    getRenderedManeuverKey(item, index),
                  )"
                  :key="assetItem.key"
                >
                  {{ $t(`reports.oscillography.asset-info.${assetItem.key}`) }}:
                  {{ formatValue(assetItem.value) }}
                </b-badge>
              </template>
            </div>
          </accordion-panel>
        </li>
      </ul>
      <b-row
        v-if="showManeuverPaginator"
        class="chart-legends__pagination-row mx-0"
      >
        <b-col cols="12" class="px-0">
          <paginator
            v-model="pagination.currentPage"
            :total-rows="visibleManeuvers.length"
            :per-page="pagination.perPage"
            size="small"
          />
        </b-col>
      </b-row>
    </accordion-panel>
  </div>
</template>

<script>
import { getColorsListByLength } from '@/domain/data/charts/chartColors';
import AccordionPanel from '@/components/AccordionPanel';
import eventBus from '@/services/globalEventBus';
import events from '@/domain/events/common';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';
import themeService from '@/services/themeService';
import { mapGetters } from 'vuex';
import oscillographyChartTypes from '@/domain/enums/oscillographyChartTypes';
import { cleanIncludes, clearDiacritics } from '@/services/stringHelper';
import Paginator from '@/components/lists/Paginator';
import InfoReportCard from
  '@/domain/components/generalPanel/reports/oscillography/infoReport/InfoReportCard';

export default {
  name: 'OscillographyChartLegends',
  props: {
    items: {
      type: Array,
      required: true,
    },
    hiddenLegends: {
      type: Array,
      default: () => [],
    },
    chartId: {
      type: String,
      required: true,
    },
    itemsUniqueProperty: {
      type: String,
      default: 'name',
    },
    colorsList: {
      type: Array,
    },
    startCollapsed: {
      type: Boolean,
      default: false,
    },
    oscillographyChartType: {
      type: String,
      default: oscillographyChartTypes.OSCILLOGRAPHY,
    },
    maneuverData: {
      type: Array,
      default: () => [],
    },
    bookmarks: {
      type: Boolean,
      default: true,
    },
    hideManeuversSection: {
      type: Boolean,
      default: false,
    },
    enableManeuverListControls: {
      type: Boolean,
      default: false,
    },
    hideInstallationAndCompanyColumns: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    AccordionPanel,
    Paginator,
    InfoReportCard,
  },
  data: vm => ({
    collapsed: vm.startCollapsed,
    secondPanelCollapsed: vm.startCollapsed,
    allLegendsSelected: true,
    allManeuversSelected: true,
    loading: false,
    collapsedItems: {},
    activeManeuvers: [],
    activeLegends: [],
    maneuverSelectionInitialized: false,
    maneuverDataFiltered: [],
    tempSearch: null,
    search: null,
    pagination: {
      currentPage: 1,
      perPage: 10,
    },
    sortBy: {
      field: 'dateTimeOscillography',
      desc: true,
    },
    oscillographyChartTypes,
  }),
  computed: {
    referenceVariablesExists() {
      return this.items.some(item => item.fieldType === 'oscillography_reference');
    },
    orderedItems() {
      const result = this.getItemsWithColors();
      return result;
    },

    isAllLegendsSelected() {
      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
        return this.activeManeuvers.length > 0
          && Object.values(this.orderedItems).every(values => !this.isLegendHidden(values));
      }

      if (this.activeManeuvers.length === 0) {
        return false;
      }

      const variablesFromActiveManeuvers = Object.values(this.orderedItems)
        .flat()
        .filter(item => this.activeManeuvers.includes(this.getChartItemManeuverKey(item)));

      if (variablesFromActiveManeuvers.length === 0) {
        return false;
      }

      return variablesFromActiveManeuvers.every(variable => (
        !this.hiddenLegends.includes(variable.name)
      ));
    },
    isAllManeuversSelected() {
      const maneuvers = this.visibleManeuvers.map(this.getManeuverKey);
      if (!maneuvers.length) {
        return false;
      }
      return maneuvers.every(item => this.activeManeuvers.includes(item));
    },
    chartColors() {
      return getColorsListByLength(this.items.length, this.colorsList);
    },
    isAllExpanded() {
      const maneuvers = this.visibleManeuvers;
      if (!maneuvers.length) {
        return false;
      }
      return (
        maneuvers.every(maneuver => !this.collapsedItems[
          this.getRenderedManeuverKey(maneuver)
        ])
      );
    },
    maneuverToggleAllLabel() {
      return this.isAllExpanded
        ? this.$t('actions.retract-all')
        : this.$t('actions.expand-all');
    },
    visibleManeuvers() {
      const baseList = this.maneuverDataFiltered;

      if (!this.enableManeuverListControls) {
        return baseList;
      }

      const filtered = this.effectiveQuery
        ? baseList.filter(maneuver => this.matchesManeuverSearch(maneuver))
        : baseList;

      return [...filtered].sort((a, b) => this.compareManeuvers(a, b));
    },
    paginatedManeuvers() {
      if (!this.showManeuverPaginator) {
        return this.visibleManeuvers;
      }

      const start = (this.pagination.currentPage - 1) * this.pagination.perPage;
      const end = start + this.pagination.perPage;
      return this.visibleManeuvers.slice(start, end);
    },
    showManeuverPaginator() {
      return this.isDiagnosticsChart
        && this.enableManeuverListControls
        && this.visibleManeuvers.length > this.pagination.perPage;
    },
    isDiagnosticsChart() {
      return this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS;
    },
    shouldShowDiagnosticsCards() {
      return this.isDiagnosticsChart && this.enableManeuverListControls;
    },
    effectiveQuery() {
      return this.search && this.search.trim()
        ? clearDiacritics(this.search)
          .toLowerCase()
        : null;
    },
    background() {
      return themeService.isDarkTheme()
        ? 'var(--color-background-dark)'
        : 'var(--color-neutral-background)';
    },
    showInstallationAndCompanyColumns() {
      return !this.hideInstallationAndCompanyColumns;
    },
    maneuverNameColumnCols() {
      return 2;
    },
    maneuverDateColumnCols() {
      return this.hideInstallationAndCompanyColumns ? 3 : 2;
    },
    maneuverAssetColumnCols() {
      return this.hideInstallationAndCompanyColumns ? 3 : 2;
    },
    maneuverModuleColumnCols() {
      if (this.hideInstallationAndCompanyColumns) {
        return 3;
      }

      return this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS ? 2 : 1;
    },
    ...mapGetters([
      'isPrinting',
    ]),
  },
  mounted() {
    this.activeLegends = this.items.map(legend => legend.name);
  },
  created() {
    this.initListeners();
  },
  watch: {
    maneuverData: {
      immediate: true,
      handler() {
        this.syncManeuverData(true);
      },
    },
    items: {
      immediate: true,
      handler() {
        this.syncManeuverData();
      },
    },
    visibleManeuvers() {
      this.normalizePagination();
    },
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    syncManeuverData(resetSelection = false) {
      const baseData = this.referenceVariablesExists
        ? this.maneuverData
        : this.maneuverData.filter(maneuver => !maneuver.isReference);

      this.maneuverDataFiltered = baseData.map((maneuver, index) => ({
        ...maneuver,
        maneuverListKey: `${this.getManeuverKey(maneuver) || 'maneuver'}_${index}`,
      }));
      const maneuverKeys = this.maneuverDataFiltered.map(this.getManeuverKey);
      if (resetSelection || !this.maneuverSelectionInitialized) {
        this.activeManeuvers = maneuverKeys;
        this.maneuverSelectionInitialized = true;
      } else {
        this.activeManeuvers = this.activeManeuvers.filter(key => maneuverKeys.includes(key));
      }
      this.collapsedItems = this.buildCollapsedItems(this.maneuverDataFiltered);
      this.normalizePagination();
    },
    searchChange(search) {
      this.search = search;
      this.pagination.currentPage = 1;
    },
    buildCollapsedItems(maneuvers) {
      return maneuvers.reduce((acc, maneuver, index) => {
        acc[this.getRenderedManeuverKey(maneuver, index)] = true;
        return acc;
      }, {});
    },
    getManeuverKey(maneuver) {
      return maneuver?.maneuverKey
        || maneuver?.maneuverUUID
        || maneuver?.maneuverId
        || maneuver?.name;
    },
    getChartItemManeuverKey(item) {
      return item?.maneuverKey || item?.oscillographyUUID;
    },
    getRenderedManeuverKey(maneuver, index = null) {
      if (maneuver?.maneuverListKey) {
        return maneuver.maneuverListKey;
      }

      const maneuverKey = this.getManeuverKey(maneuver) || 'maneuver';
      return index === null || index === undefined
        ? maneuverKey
        : `${maneuverKey}_${index}`;
    },
    getManeuverCheckboxKey(maneuver, index = null) {
      return `${this.getRenderedManeuverKey(maneuver, index)}-${this.isManeuverHidden(
        this.getManeuverKey(maneuver),
      )}`;
    },
    filteredAssetInfo(maneuverKey) {
      const maneuver = this.maneuverDataFiltered.find(
        item => (item.maneuverListKey || this.getManeuverKey(item)) === maneuverKey,
      );
      const assetInfo = maneuver?.assetInfo || [];
      return assetInfo.filter(item => item.value !== null && item.value !== '');
    },
    matchesManeuverSearch(maneuver) {
      const searchableValues = [
        maneuver.name,
        maneuver.dateTimeOscillography,
        this.formatDateTime(maneuver.dateTimeOscillography),
        maneuver.assetName,
      ].filter(Boolean);

      if (this.showInstallationAndCompanyColumns) {
        searchableValues.push(maneuver.installationName, maneuver.companyName);
      }

      searchableValues.push(maneuver.moduleName);

      return searchableValues.some(value => cleanIncludes(
        value,
        this.effectiveQuery,
        {
          clearValueToFind: false,
        },
      ));
    },
    compareManeuvers(a, b) {
      const { field, desc } = this.sortBy;
      const direction = desc ? -1 : 1;

      const aValue = this.getSortableManeuverValue(a, field);
      const bValue = this.getSortableManeuverValue(b, field);

      if (aValue === bValue) {
        return 0;
      }

      if (aValue === null || aValue === undefined) {
        return 1 * direction;
      }

      if (bValue === null || bValue === undefined) {
        return -1 * direction;
      }

      if (field === 'dateTimeOscillography') {
        return (new Date(aValue).getTime() - new Date(bValue).getTime()) * direction;
      }

      return String(aValue).localeCompare(String(bValue), undefined, {
        sensitivity: 'base',
      }) * direction;
    },
    getSortableManeuverValue(maneuver, field) {
      if (!maneuver) {
        return null;
      }

      switch (field) {
        case 'dateTimeOscillography':
          return maneuver.dateTimeOscillography;
        case 'name':
          return maneuver.name;
        case 'assetName':
          return maneuver.assetName;
        case 'installationName':
          return maneuver.installationName;
        case 'companyName':
          return maneuver.companyName;
        case 'moduleName':
          return maneuver.moduleName;
        default:
          return maneuver[field];
      }
    },
    sortByField(field) {
      if (!this.enableManeuverListControls) {
        return;
      }

      if (this.sortBy.field === field) {
        this.sortBy = {
          ...this.sortBy,
          desc: !this.sortBy.desc,
        };
        this.pagination.currentPage = 1;
        return;
      }

      this.sortBy = {
        field,
        desc: field === 'dateTimeOscillography',
      };
      this.pagination.currentPage = 1;
    },
    normalizePagination() {
      if (!this.showManeuverPaginator) {
        this.pagination.currentPage = 1;
        return;
      }

      const totalPages = Math.max(
        1,
        Math.ceil(this.visibleManeuvers.length / this.pagination.perPage),
      );

      if (this.pagination.currentPage > totalPages) {
        this.pagination.currentPage = totalPages;
      }

      if (this.pagination.currentPage < 1) {
        this.pagination.currentPage = 1;
      }
    },
    getDiagnosticsFields(maneuver) {
      return (maneuver?.infoData?.diagnostics || []).filter(field => field.hasValue);
    },
    getDiagnosticsFieldValue(value) {
      if (value === null || value === undefined) {
        return '';
      }

      return String(this.formatValue(value));
    },
    isDiagnosticFieldAcceptable(field) {
      if (field.acceptable === false) {
        return false;
      }

      const value = this.toDiagnosticNumber(field.rawValue ?? field.value);
      const lowerLimit = this.toDiagnosticNumber(field.limiteInferior);
      const upperLimit = this.toDiagnosticNumber(field.limiteSuperior);

      if (value === null) {
        return true;
      }

      return (lowerLimit === null || value >= lowerLimit)
        && (upperLimit === null || value <= upperLimit);
    },
    toDiagnosticNumber(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const normalizedValue = typeof value === 'string'
        ? value.replace(',', '.')
        : value;
      const numericValue = Number(normalizedValue);

      return Number.isFinite(numericValue) ? numericValue : null;
    },
    getSortIconClass(field) {
      if (!this.enableManeuverListControls) {
        return '';
      }

      if (this.sortBy.field !== field) {
        return 'fas fa-sort chart-legends__sort-icon';
      }

      return this.sortBy.desc
        ? 'fas fa-sort-down chart-legends__sort-icon chart-legends__sort-icon--active'
        : 'fas fa-sort-up chart-legends__sort-icon chart-legends__sort-icon--active';
    },
    previewContent(typeLine, width, symbol, symbolColor) {
      let typeSymbol = '';

      switch (symbol) {
        case 'circle':
          typeSymbol = `<circle cx="50%" cy="10" r="10" fill="${symbolColor}" />`;
          break;
        case 'rect':
          typeSymbol = '<rect width="14" height="14" x="40%" y="2" '
            + `style="fill:${symbolColor};stroke-width:3;" />`;
          break;
        case 'triangle':
          typeSymbol = '<polygon points="38,0 45,20 30,20" '
            + `style="fill:${symbolColor};stroke-width:3" />`;
          break;
        case 'diamond':
          typeSymbol = '<polygon points="38,0 50,10 38,20 26,10" '
            + `style="fill:${symbolColor};stroke-width:3" />`;
          break;
        default:
          typeSymbol = '';
      }

      return `
        <svg width="100%" height="20">
          <line x1="0" y1="10" x2="100%" y2="10"
            stroke="${this.getStrokeColor()}"
            stroke-width="${!width ? 6 : width}"
            stroke-linejoin="bevel"
            stroke-dasharray="${!typeLine ? '' : typeLine}"
          />${typeSymbol}
        </svg>
      `;
    },
    getStrokeColor() {
      if (this.isPrinting) return '#000';
      return themeService.isDarkTheme() ? '#FFF' : '#000';
    },
    handleAccordionOnPrint() {
      const originalState = this.collapsed;
      window.onbeforeprint = () => {
        this.openAccordion();
      };
      window.onafterprint = () => {
        this.collapsed = originalState;
      };
    },
    handleSecondPanelAccordionOnPrint() {
      const originalState = this.secondPanelCollapsed;
      window.onbeforeprint = () => {
        this.openAccordion();
      };
      window.onafterprint = () => {
        this.secondPanelCollapsed = originalState;
      };
    },
    openAccordion() {
      this.collapsed = false;
    },
    openSecondPanelAccordion() {
      this.secondPanelCollapsed = false;
    },
    initListeners() {
      eventBus.on(events.print, this.openAccordion);
    },
    removeListeners() {
      eventBus.off(events.print, this.openAccordion);
    },
    getVisibilityStyle(variable) {
      return {
        display: (variable.values || []).length ? 'block' : 'none',
      };
    },
    getColorStyle(color) {
      return {
        'background-color': color,
      };
    },
    toggleLegend(values, isChecked) {
      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
        if (!isChecked) {
          this.activeLegends = this.activeLegends.filter(
            legend => !values.some(v => v.name === legend),
          );
          this.$emit('toggle-legend', values);
        } else {
          this.activeLegends = [
            ...this.activeLegends,
            ...values.map(item => item.name),
          ];
          this.$emit('toggle-legend', values);
        }
        return;
      }

      const actives = this.activeLegends.filter(
        legend => legend.includes(values[0].name.split(' - ')[4]),
      );

      if (!isChecked) {
        const filteredValues = values.filter(
          value => !this.hiddenLegends.includes(value.name),
        );

        this.activeLegends = this.activeLegends.filter(
          legend => !actives.includes(legend),
        );
        this.$emit('toggle-legend', filteredValues);

        const affectedManeuvers = [...new Set(values.map(this.getChartItemManeuverKey))];
        affectedManeuvers.forEach(maneuverKey => {
          if (this.shouldDeactivateManeuver(maneuverKey)) {
            this.activeManeuvers = this.activeManeuvers.filter(
              activeManeuverKey => activeManeuverKey !== maneuverKey,
            );
          }
        });
      } else {
        this.activeLegends = [
          ...this.activeLegends,
          ...values.map(item => item.name),
        ];

        const variablesToShow = values
          .filter(value => this.hiddenLegends.includes(value.name))
          .filter(value => this.activeManeuvers.includes(
            this.getChartItemManeuverKey(value),
          ));

        this.$emit('toggle-legend', variablesToShow);

        const affectedManeuvers = [...new Set(values.map(this.getChartItemManeuverKey))];
        affectedManeuvers.forEach(maneuverKey => {
          const hasActiveVars = this.hasActiveVariablesForManeuver(maneuverKey);
          const isNotActive = !this.activeManeuvers.includes(maneuverKey);
          if (hasActiveVars && isNotActive) {
            this.activeManeuvers = [...this.activeManeuvers, maneuverKey];
          }
        });
      }
    },
    toggleManeuvers(maneuverKey, isChecked) {
      if (!maneuverKey) return;
      this.$emit('toggle-maneuver', maneuverKey);
      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
        if (!isChecked) {
          this.activeManeuvers = this.activeManeuvers.filter(
            activeManeuverKey => activeManeuverKey !== maneuverKey,
          );

          if (this.activeManeuvers.length === 0) {
            const allVisibleVariables = Object.values(this.orderedItems)
              .flat()
              .filter(item => !this.hiddenLegends.includes(item.name));

            if (allVisibleVariables.length > 0) {
              this.$emit('toggle-legend', allVisibleVariables);
            }
          }
        } else {
          this.activeManeuvers = [...this.activeManeuvers, maneuverKey];

          this.$nextTick(() => {
            const variablesToShow = Object.values(this.orderedItems)
              .flat()
              .filter(item => this.activeLegends.includes(item.name)
                && this.hiddenLegends.includes(item.name));

            if (variablesToShow.length > 0) {
              this.$emit('toggle-legend', variablesToShow);
            }
          });
        }
        return;
      }
      const values = Object.values(this.orderedItems)
        .flat()
        .filter(item => this.getChartItemManeuverKey(item) === maneuverKey);
      if (!isChecked) {
        const currentManeuvers = this.activeManeuvers.filter(
          activeManeuverKey => activeManeuverKey !== maneuverKey,
        );
        this.activeManeuvers = currentManeuvers;
        const filteredValues = values.filter(
          // eslint-disable-next-line comma-dangle
          value => !this.hiddenLegends.includes(value.name),
        );
        this.$emit('toggle-legend', filteredValues);
      } else {
        this.activeManeuvers = [...this.activeManeuvers, maneuverKey];
        const variablesToShow = values
          .filter(value => this.activeLegends.includes(value.name))
          .filter(value => this.hiddenLegends.includes(value.name));

        this.$emit('toggle-legend', variablesToShow);
      }
    },
    isLegendHidden(values) {
      if (!Array.isArray(values)) {
        return false;
      }
      const variables = values.map(value => value[this.itemsUniqueProperty]);
      return variables.every(variable => this.hiddenLegends.includes(variable));
    },
    isManeuverHidden(maneuverKey) {
      return this.activeManeuvers.includes(maneuverKey);
    },
    checkAllLegends(event) {
      if (event) {
        if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
          this.activeLegends = Object.values(this.orderedItems).flat().map(item => item.name);
          this.$emit('select-all-legends');
          return;
        }

        this.activeLegends = [
          ...this.activeLegends,
          ...Object.values(this.orderedItems).flat().map(item => item.name),
        ];

        const variablesToShow = Object.values(this.orderedItems)
          .flat()
          .filter(item => this.activeManeuvers.includes(this.getChartItemManeuverKey(item))
            && this.hiddenLegends.includes(item.name));

        this.$emit('toggle-legend', variablesToShow);
        return;
      }

      this.activeLegends = [];
      this.$emit('unselect-all-legends');

      if (this.oscillographyChartType !== oscillographyChartTypes.DIAGNOSTICS) {
        const maneuversToDeactivate = this.activeManeuvers.filter(maneuverKey => (
          this.shouldDeactivateManeuver(maneuverKey)
        ));

        this.activeManeuvers = this.activeManeuvers.filter(maneuverKey => (
          !maneuversToDeactivate.includes(maneuverKey)
        ));
      }
    },
    checkAllManeuvers(event) {
      if (event) {
        const maneuversToSelect = this.visibleManeuvers.map(this.getManeuverKey);
        this.activeManeuvers = [...new Set([
          ...this.activeManeuvers,
          ...maneuversToSelect,
        ])];
        this.allManeuversSelected = true;

        if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
          const variablesToShow = Object.values(this.orderedItems)
            .flat()
            .filter(item => this.activeLegends.includes(item.name)
              && this.hiddenLegends.includes(item.name));

          this.$emit('toggle-all-maneuvers', true);
          if (variablesToShow.length > 0) {
            this.$emit('toggle-legend', variablesToShow);
          }
          return;
        }

        const variablesToShow = Object.values(this.orderedItems)
          .flat()
          .filter(item => this.activeLegends.includes(item.name)
            && this.hiddenLegends.includes(item.name));
        this.$emit('toggle-legend', variablesToShow);
        return;
      }

      this.allManeuversSelected = false;
      const maneuversToUnselect = this.visibleManeuvers.map(this.getManeuverKey);
      this.activeManeuvers = this.activeManeuvers.filter(
        maneuverKey => !maneuversToUnselect.includes(maneuverKey),
      );

      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
        this.$emit('toggle-all-maneuvers', false);

        if (this.activeManeuvers.length === 0) {
          const allVisibleVariables = Object.values(this.orderedItems)
            .flat()
            .filter(item => !this.hiddenLegends.includes(item.name));

          if (allVisibleVariables.length > 0) {
            this.$emit('toggle-legend', allVisibleVariables);
          }
        }
        return;
      }

      const allVisibleVariables = Object.values(this.orderedItems)
        .flat()
        .filter(item => !this.hiddenLegends.includes(item.name));
      this.$emit('toggle-legend', allVisibleVariables);
    },
    shouldDeactivateManeuver(maneuverKey) {
      const maneuverVariables = Object.values(this.orderedItems)
        .flat()
        .filter(item => this.getChartItemManeuverKey(item) === maneuverKey);

      return maneuverVariables.every(variable => this.hiddenLegends.includes(variable.name));
    },
    hasActiveVariablesForManeuver(maneuverKey) {
      const maneuverVariables = Object.values(this.orderedItems)
        .flat()
        .filter(item => this.getChartItemManeuverKey(item) === maneuverKey);

      return maneuverVariables.some(variable => !this.hiddenLegends.includes(variable.name));
    },
    getItemsWithColors() {
      let filteredItems;
      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
        filteredItems = this.items.filter(item => Array.isArray(item.values));
      } else {
        filteredItems = this.items.filter(item => Array.isArray(item.values)
        && item.values.length > 0);
      }
      const groupedItems = filteredItems.reduce((acc, item) => {
        const variableName = this.isDiagnosticsChart
          ? item.variableName || item[this.itemsUniqueProperty] || item.name
          : item.variableName;

        if (!acc[variableName]) {
          acc[variableName] = [];
        }
        acc[variableName].push(item);

        return acc;
      }, {});

      const sortedGroupedItems = Object.keys(groupedItems)
        .sort((a, b) => {
          const aOrder = groupedItems[a][0]?.order;
          const bOrder = groupedItems[b][0]?.order;

          if (Number.isFinite(aOrder) && Number.isFinite(bOrder) && aOrder !== bOrder) {
            return aOrder - bOrder;
          }

          if (Number.isFinite(aOrder) && !Number.isFinite(bOrder)) {
            return -1;
          }

          if (!Number.isFinite(aOrder) && Number.isFinite(bOrder)) {
            return 1;
          }

          return 0;
        })
        .reduce((acc, key) => {
          acc[key] = groupedItems[key];
          return acc;
        }, {});

      const groupedItemsWithColors = Object.keys(sortedGroupedItems).map(
        (variableName, index) => {
          const groupItems = sortedGroupedItems[variableName];
          const colorList = getColorsListByLength(
            Object.keys(sortedGroupedItems).length,
          );
          const color = colorList[index];
          return groupItems.map(item => ({
            ...item,
            chartColor: color,
          }));
        },
      );

      const finalGroupedItems = groupedItemsWithColors.reduce(
        (acc, groupItems) => {
          const { variableName } = groupItems[0];
          acc[variableName] = groupItems;
          return acc;
        },
        {},
      );

      return finalGroupedItems;
    },

    toggleCollapse(index) {
      this.$set(this.collapsedItems, index, !this.collapsedItems[index]);
    },
    toggleCollapsePrint(index) {
      this.$set(this.collapsedItems, index, false);
    },
    toogleAccordions() {
      const maneuvers = this.visibleManeuvers;
      if (this.isAllExpanded) {
        this.collapsedItems = this.buildCollapsedItems(maneuvers);
      } else {
        this.collapsedItems = maneuvers.reduce((acc, maneuver, index) => {
          acc[this.getRenderedManeuverKey(maneuver, index)] = false;
          return acc;
        }, {});
      }
    },
    formatValue(value) {
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(value)) {
        return value;
      }
      if (this.isDate(value)) {
        return this.formatDateTime(value);
      }
      return value;
    },
    isDate(value) {
      // eslint-disable-next-line no-restricted-globals
      return !isNaN(Date.parse(value));
    },
    formatDateTime(dateTime) {
      return dateTimeFormatter(dateTime, formats.DATETIME_SHORT_WITH_SECONDS);
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"
.chart-legends
  border: 1px solid $color-border-gray
  border-radius: 5px
  ::v-deep &__header
    min-height: 25px
    padding: 0 15px !important
    background-color: $color-gray-medium-light
  &__search-row
    padding: 0 0 .25rem
  &__search-col
    display: flex
    justify-content: flex-end
  &__maneuvers-panel
    margin-top: .5rem !important
  &__pagination-row
    margin-top: 1rem
  &__maneuver-details
    display: flex
    flex-wrap: wrap
    justify-content: center
  &__sort-button
    display: inline-flex
    align-items: center
    gap: 4px
    padding: 0
    border: 0
    background: transparent
    color: inherit
    text-align: left
    cursor: pointer
    outline: none
  &__sort-icon
    font-size: 0.85rem
  &__disabled
    opacity: .5
  &__list
    position: relative
    padding-left: 20px
    column-count: 2
    list-style-type: none
    margin-bottom: 0
    +media-max-md
      column-count: 1
  &__checkbox
    display: inline-flex
    vertical-align: middle
  &__list-item
    margin-bottom: 10px
    display: flex
    align-items: center
  &__toggle-all
    margin-bottom: 10px
    margin-left: 20px
    display: inline-block
  &__checkbox-label
    vertical-align: middle
    white-space: normal
    word-break: break-word
  &__accordion-label
    font-size: 0.8rem
  &__color
    display: inline-block
    vertical-align: middle
    margin: 5px 3px 5px 0
    width: 15px
    height: 15px
.preview
    width: 85px
    height: 50px
    border: 1px solid $color-border-gray
    padding: 5px
    display: flex
    align-items: center
    justify-content: center
    text-align: center
    & strong
      font-size: 0.8rem
.assetBadge
  background-color: $color-gray-medium-highlight
  margin-left: 5px
</style>
